/** ****************************************************************************
 * IFramePostMessageService handles all incoming iFrame PostMessages.
 *
 * Key messages to be handled are
 * - Known messages that can be suppressed
 * - Unknown messages that need to be investigated
 * - A response from the partner containing a TransactionInformation
 *
 * @file   Global variables under a single namespace
 * @author Paul J Stales
 * @since  2.0.0
 **************************************************************************** */

import { D, ERROR_MESSAGES } from '../../../../common/constants/ConstantsLogs'
import {
  BPERX_STRING,
  THIRD_PARTY_MESSAGE_TYPE
} from '../../../../common/constants/ConstantsValues'
import {
  IFramePostMessage,
  IFramePostMessageType
} from '../../../../common/types/IFramePostMessage'
import { ReportableEvent } from '../../../../common/types/reportableEvents/ReportableEvent'
import { TransactionInformation } from '../../../../common/types/transactionInformationTypes/TransactionInformation'
import { _devLog } from '../../../../common/utilities/DevLoggingUtility'

/**
 * The parent origin, derived from document.referrer at load time.
 * Used as the target origin for all outgoing postMessage calls
 * and to validate incoming message origins.
 */
export const PARENT_ORIGIN: string = (() => {
  try {
    return new URL(document.referrer).origin
  } catch {
    return ''
  }
})()

/**
 * Handle unexpected, non-typed messages as best as possible.
 *
 * Some non-typed messages are known to bPerx and suppressed,
 * such as Preact warnings or messages.
 *
 * If the message type is missing, show an error, it will need
 * to be investigated. Then we can either handle or suppress it
 * once we are aware of it.
 */
export function handleNonTypedMessage(
  messageEvent: MessageEvent,
  partnerId: string
): void {
  if (
    messageEvent.data.source ==
      THIRD_PARTY_MESSAGE_TYPE.SUPPRESS_PREACT_DEVTOOLS ||
    messageEvent.data.source ==
      THIRD_PARTY_MESSAGE_TYPE.SUPPRESS_PREACT_PAGEHOOK ||
    messageEvent.data.type == THIRD_PARTY_MESSAGE_TYPE.SUPPRESS_WEBPACK_OK ||
    messageEvent.data.type == THIRD_PARTY_MESSAGE_TYPE.SUPPRESS_WEBPACK_WARNINGS
    // || messageEvent.data.type == THIRD_PARTY_MESSAGE_TYPE.SUPPRESS_WEBPACK_INVALID
  ) {
    _devLog(D.IFRAME_POSTMSG_SUPPRESSED, partnerId, undefined)
  } else {
    console.error(ERROR_MESSAGES.ERROR_5001_MESSAGE_TYPE_MISSING)
  }
}

/**
 * Handle the message response.
 * @param messageEvent
 * @param partnerId
 * @param bindFlag
 * @param bindInfo
 */
export function handleMessageResponse(
  messageEvent: MessageEvent,
  partnerId: string,
  bindFlag: (b: boolean) => void,
  bindInfo: (info: any) => void
): void {
  const { messageType, payload } = messageEvent.data
  _devLog(D.IFRAME_HANDLE_MESSAGE_RESPONSE, partnerId, undefined)
  _devLog(`MSG TYPE: ${messageType}`, partnerId, undefined)
  _devLog(payload, partnerId, undefined)

  bindInfo(payload)
  bindFlag(true)
}

/**
 * The bPerx iFrame requests PartnerSuppliedInfo from the parent.
 * @param partnerId
 */
export function requestPartnerInfoFromParent(partnerId: string): void {
  _devLog(D.IFRAME_REQUEST_PSI, partnerId, undefined)
  const POSTMESSAGE_PARTNER_INFO_REQUEST: Partial<IFramePostMessage> = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.PARTNER_INFO_REQUEST,
    payload: null
  }
  parent.postMessage(POSTMESSAGE_PARTNER_INFO_REQUEST, PARENT_ORIGIN)
}

/**
 * The bPerx iFrame requests TransactionInformation from the parent.
 * @param partnerId
 */
export function requestTransactionInformationFromLocalStorage(
  partnerId: string
): void {
  _devLog(D.IFRAME_REQUEST_TI, partnerId, undefined)
  const POSTMESSAGE_TRANSACTION_INFO_REQUEST: IFramePostMessage = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.TRANSACTION_INFORMATION_REQUEST,
    iFrameLocalStorageKey: getLocalStorageKeyFromUrlParameters(),
    payload: null
  }
  parent.postMessage(POSTMESSAGE_TRANSACTION_INFO_REQUEST, PARENT_ORIGIN)
}

/**
 * Sends the parent window (the partner's website) the height of the iFrame.
 *
 * Partners include bperx.js in their website via SCRIPT SRC bperx.js.
 * Partners use a SCRIPT to call displayOffer() from bperx.js, which loads the iFrame.
 * As the iFrame changes height because of "onload, resize, products visibility",
 * this file as part of the iFrame needs to tell the partner its new height via postMessage.
 * Partner receives the postMessage, and updates the HTML element that contains the iframe.
 * @param height
 * @param partnerId
 */
export function sendFrameHeightViaPostMessage(
  height: number,
  partnerId: string
): void {
  const POSTMESSAGE_FRAME_HEIGHT_UPDATE: IFramePostMessage = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.IFRAME_HEIGHT_UPDATE,
    iFrameLocalStorageKey: getLocalStorageKeyFromUrlParameters(),
    payload: height
  }
  //_devLog(D.IFRAME_SEND_HEIGHT, partnerId, undefined)
  parent.postMessage(POSTMESSAGE_FRAME_HEIGHT_UPDATE, PARENT_ORIGIN)
}

/**
 * Sends a PostMessage to the partner, with the latest changes to the
 * TransactionInformation object.
 * @param transactionInformation
 * @param partnerId
 */
export function updateTransactionInformationViaPostMessage(
  transactionInformation: TransactionInformation,
  partnerId: string
): void {
  const POSTMESSAGE_TRANSACTION_INFO_UPDATE: IFramePostMessage = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.TRANSACTION_INFORMATION_UPDATE,
    iFrameLocalStorageKey: getLocalStorageKeyFromUrlParameters(),
    payload: transactionInformation
  }
  _devLog(D.IFRAME_SEND_TI, partnerId, undefined)
  parent.postMessage(POSTMESSAGE_TRANSACTION_INFO_UPDATE, PARENT_ORIGIN)
}
function getLocalStorageKeyFromUrlParameters(): string {
  return location.href.slice(1 + location.href.indexOf('='))
}

/**
 * Send a message to the partner, informing them it is time
 * to delete TransactionInformation from localstorage.
 * @param partnerId
 */
export function deleteTransactionInformationViaPostMessage(
  partnerId: string
): void {
  const POSTMESSAGE_TRANSACTION_INFO_DELETE: IFramePostMessage = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.TRANSACTION_INFORMATION_DELETE,
    iFrameLocalStorageKey: getLocalStorageKeyFromUrlParameters(),
    payload: null
  }
  _devLog(D.IFRAME_DELETE_TI, partnerId, undefined)
  parent.postMessage(POSTMESSAGE_TRANSACTION_INFO_DELETE, PARENT_ORIGIN)
}

/**
 * Send a reportable event to the partner.
 * @param reportableEvent
 * @param partnerId
 */
export function sendReportableEventViaPostMessage(
  reportableEvent: ReportableEvent,
  partnerId: string
): void {
  const POSTMESSAGE_REPORTABLE_EVENT: IFramePostMessage = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.REPORTABLE_EVENT,
    iFrameLocalStorageKey: getLocalStorageKeyFromUrlParameters(),
    payload: reportableEvent
  }
  _devLog(D.REPORTABLE_EVENT, partnerId, undefined)
  parent.postMessage(POSTMESSAGE_REPORTABLE_EVENT, PARENT_ORIGIN)
}

/**
 * Display a clickable popup with the name and image of
 * the digital magazine, containing a link read the digital magazine.
 * @param eMagazineUrl
 * @param magazineImage
 * @param magazineTitle
 * @param partnerId
 */
export function sendDigitalLinkViaPostMessage(
  eMagazineUrl: string,
  magazineImage: string,
  magazineTitle: string,
  partnerId: string
): void {
  const PAYLOAD_DIGITAL_LINK = {
    eMagazineUrl,
    magazineImage,
    magazineTitle
  }
  const POSTMESSAGE_SEND_DIGITAL_LINK: IFramePostMessage = {
    origin: BPERX_STRING,
    messageType: IFramePostMessageType.IFRAME_DIGITAL_LINK_SEND,
    iFrameLocalStorageKey: getLocalStorageKeyFromUrlParameters(),
    payload: PAYLOAD_DIGITAL_LINK
  }
  _devLog(D.IFRAME_DIGITALLINK_SENT, partnerId, undefined)
  parent.postMessage(POSTMESSAGE_SEND_DIGITAL_LINK, PARENT_ORIGIN)
}
